import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, SectionCard } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type { VendorApplicationAdminDetail } from '@/lib/api-admin/types';
import {
  useVendorCredentialIssueMutation,
  useVendorCredentialResetMutation,
} from '../hooks/useVendorCredentialIssueMutation';
import { useVendorApplicationQuery } from '../hooks/useVendorApplicationQuery';
import { useVendorReviewActionMutation } from '../hooks/useVendorReviewActionMutation';
import type {
  VendorCredentialInfo,
  VendorCredentialIssueResult,
} from '../types/vendorCredential';
import { VendorApplicationStatusPill } from '../components/VendorApplicationStatusPill';
import { VendorReviewLogTimeline } from '../components/VendorReviewLogTimeline';
import { VendorKycDocumentList } from '../components/VendorKycDocumentList';
import { VendorApproveModal } from '../components/VendorApproveModal';
import { VendorRejectModal } from '../components/VendorRejectModal';
import { VendorRequestResubmissionModal } from '../components/VendorRequestResubmissionModal';
import { VendorCredentialIssueModal } from '../components/VendorCredentialIssueModal';

type ModalKind = 'approve' | 'reject' | 'resubmission' | 'credentials' | 'reset' | null;

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) {
    return err.message ?? err.code;
  }
  if (err instanceof Error) return err.message;
  return null;
};

const DataRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
    <dt className="col-span-1 text-[color:var(--content-muted)]">{label}</dt>
    <dd className="col-span-2 text-[color:var(--content-default)]">{value ?? '—'}</dd>
  </div>
);

export const VendorApplicationDetailPage = () => {
  const { t } = useTranslation('vendors');
  const navigate = useNavigate();
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId;

  const query = useVendorApplicationQuery(applicationId);
  const reviewMutation = useVendorReviewActionMutation();
  const credentialMutation = useVendorCredentialIssueMutation();
  const resetMutation = useVendorCredentialResetMutation();

  const [openModal, setOpenModal] = useState<ModalKind>(null);
  const [credentialResult, setCredentialResult] =
    useState<VendorCredentialIssueResult | null>(null);

  if (query.isPending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="py-12 text-center text-sm text-[color:var(--content-muted)]"
      >
        {t('vendors.detail.loading')}
      </div>
    );
  }

  if (query.isError || !query.data || !applicationId) {
    return (
      <div
        role="alert"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--status-danger-bg)',
          color: 'var(--status-danger-fg)',
        }}
      >
        <p>{t('vendors.detail.error')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/vendors')}>
          {t('vendors.actions.backToList')}
        </Button>
      </div>
    );
  }

  const detail: VendorApplicationAdminDetail = query.data;
  const isUnderReview = detail.status === 'UNDER_REVIEW';
  const isSubmitted = detail.status === 'SUBMITTED';
  // G-1(9차): 소속사 입점 승인 → 계정/자격증명 발급. APPROVED 단계에서 [발급] 버튼 노출.
  // 발급 후 status=CREDENTIALS_ISSUED 로 전이되며, 상세 응답에 credentialInfo(loginId/메타)가
  // 인라인 포함된다(미발급이면 키 생략, passwordRetrievable=false). 비번 평문은 발급/재발급 응답
  // 1회만 노출 → 분실 시 [재발급]. (be-vendor 계약 §2-1, Lead 결정 ⓒ.)
  const isApproved = detail.status === 'APPROVED';
  // generated 타입엔 아직 credentialInfo 가 없어 로컬 타입으로 안전 추출.
  const credentialInfo = (detail as { credentialInfo?: VendorCredentialInfo }).credentialInfo;
  const hasCredentials = Boolean(credentialInfo?.loginId);

  const closeModals = () => {
    setOpenModal(null);
    reviewMutation.reset();
    credentialMutation.reset();
    resetMutation.reset();
    setCredentialResult(null);
  };

  const handleStartReview = () => {
    reviewMutation.mutate({
      applicationId,
      action: 'start-review',
      body: undefined,
    });
  };

  const reviewError = errorMessageOf(reviewMutation.error);
  const isResetMode = openModal === 'reset';
  const activeCredentialMutation = isResetMode ? resetMutation : credentialMutation;
  const credentialError = errorMessageOf(activeCredentialMutation.error);

  return (
    <PageShell
      data-testid="vendor-detail"
      title={
        <span className="flex flex-wrap items-center gap-2">
          {detail.vendorName}
          <VendorApplicationStatusPill
            status={detail.status}
            displayStatus={detail.displayStatus}
            applicationId={detail.applicationId}
          />
          <span className="text-xs font-normal text-[color:var(--content-muted)]">
            {t('vendors.detail.subject', {
              businessRegistrationNoMasked: detail.businessRegistrationNoMasked,
              applicantName: detail.applicantName,
            })}
          </span>
        </span>
      }
      breadcrumb={[
        { label: t('vendors.list.title'), href: '/vendors' },
        { label: detail.vendorName },
      ]}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {isSubmitted ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartReview}
              disabled={reviewMutation.isPending}
            >
              {t('vendors.actions.startReview')}
            </Button>
          ) : null}
          {isUnderReview ? (
            <>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setOpenModal('reject')}
                disabled={reviewMutation.isPending}
                data-testid="vendor-reject-btn"
              >
                {t('vendors.actions.reject')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpenModal('resubmission')}
                disabled={reviewMutation.isPending}
                data-testid="vendor-resubmit-btn"
              >
                {t('vendors.actions.requestResubmission')}
              </Button>
              <Button
                variant="cta"
                size="sm"
                onClick={() => setOpenModal('approve')}
                disabled={reviewMutation.isPending}
                data-testid="vendor-approve-btn"
              >
                {t('vendors.actions.approve')}
              </Button>
            </>
          ) : null}
          {/*
            G-1(9차): APPROVED → [자격증명 발급]. 발급 응답에 loginId+oneTimePassword(평문 1회) 노출.
            이미 발급된 소속사(credentialInfo 존재)는 [비밀번호 재발급]만(loginId 유지, 새 1회용 비번).
          */}
          {isApproved && !hasCredentials ? (
            <Button
              variant="cta"
              size="sm"
              onClick={() => setOpenModal('credentials')}
              disabled={credentialMutation.isPending}
              data-testid="vendor-issue-credentials-btn"
            >
              {t('vendors.actions.issueCredentials')}
            </Button>
          ) : null}
          {hasCredentials ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpenModal('reset')}
              disabled={resetMutation.isPending}
              data-testid="vendor-reset-credentials-btn"
            >
              {t('vendors.actions.resetCredentials')}
            </Button>
          ) : null}
        </div>
      }
    >

      {reviewError && openModal === null ? (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-md p-3 text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {reviewError}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title={t('vendors.detail.sections.applicant')}>
          <dl>
            <DataRow label={t('vendors.fields.applicantName')} value={detail.applicantName} />
            <DataRow label={t('vendors.fields.applicantEmail')} value={detail.applicantEmail} />
            <DataRow label={t('vendors.fields.applicantPhone')} value={detail.applicantPhone} />
          </dl>
        </SectionCard>

        <SectionCard title={t('vendors.detail.sections.business')}>
          <dl>
            <DataRow label={t('vendors.fields.vendorName')} value={detail.vendorName} />
            <DataRow
              label={t('vendors.fields.businessType')}
              value={t(`vendors.businessType.${detail.businessType}`, { defaultValue: '-' })}
            />
            <DataRow
              label={t('vendors.fields.businessRegistrationNo')}
              value={
                <span data-pii="true" className="font-mono">
                  {detail.businessRegistrationNo}
                </span>
              }
            />
            <DataRow
              label={t('vendors.fields.representativeName')}
              value={detail.representativeName}
            />
            <DataRow label={t('vendors.fields.contactEmail')} value={detail.contactEmail} />
            <DataRow label={t('vendors.fields.contactPhone')} value={detail.contactPhone} />
            <DataRow
              label={t('vendors.fields.businessAddress')}
              value={`${detail.businessAddress}${
                detail.businessAddressDetail ? ` ${detail.businessAddressDetail}` : ''
              }`}
            />
            <DataRow
              label={t('vendors.fields.businessCategory')}
              value={detail.businessCategory}
            />
          </dl>
        </SectionCard>

        <SectionCard title={t('vendors.detail.sections.bankAccount')}>
          {detail.bankAccount ? (
            <dl>
              <DataRow
                label={t('vendors.fields.bankName')}
                value={`${detail.bankAccount.bankName} (${detail.bankAccount.bankCode})`}
              />
              <DataRow
                label={t('vendors.fields.accountNumber')}
                value={
                  <span data-pii="true" className="font-mono">
                    {detail.bankAccount.accountNumberMasked}
                  </span>
                }
              />
              <DataRow
                label={t('vendors.fields.accountHolder')}
                value={detail.bankAccount.accountHolder}
              />
              <DataRow
                label={t('vendors.fields.verifiedAt')}
                value={
                  detail.bankAccount.verifiedAt
                    ? new Date(detail.bankAccount.verifiedAt).toLocaleString('ko-KR')
                    : t('vendors.fields.unverified')
                }
              />
            </dl>
          ) : (
            <p className="text-sm text-[color:var(--content-muted)]">
              {t('vendors.detail.bankAccount.empty')}
            </p>
          )}
        </SectionCard>

        <SectionCard title={t('vendors.detail.sections.kyc')}>
          <VendorKycDocumentList
            documents={detail.kycDocuments}
            downloadUrls={detail.kycDocumentDownloadUrls}
          />
        </SectionCard>

        {/* G-1(9차): 발급된 소속사 계정 정보. 비번 평문은 재조회 불가 → 자리엔 [재발급] 안내. */}
        {hasCredentials && credentialInfo ? (
          <SectionCard title={t('vendors.detail.sections.credentials')} data-testid="vendor-credential-info">
            <dl>
              <DataRow
                label={t('vendors.credential.loginId', '로그인 ID')}
                value={<span className="font-mono">{credentialInfo.loginId}</span>}
              />
              <DataRow
                label={t('vendors.credential.password', '비밀번호')}
                value={t('vendors.credential.passwordNotRetrievable', '재조회 불가 — 분실 시 [재발급]')}
              />
              <DataRow
                label={t('vendors.fields.contactEmail')}
                value={credentialInfo.initialAdminEmail ?? '—'}
              />
              <DataRow
                label={t('vendors.credential.issuedAt', '발급 일시')}
                value={
                  credentialInfo.issuedAt
                    ? new Date(credentialInfo.issuedAt).toLocaleString('ko-KR')
                    : '—'
                }
              />
            </dl>
          </SectionCard>
        ) : null}

        <SectionCard title={t('vendors.detail.sections.timeline')}>
          <VendorReviewLogTimeline logs={detail.reviewLogs} />
        </SectionCard>
      </div>

      <VendorApproveModal
        isOpen={openModal === 'approve'}
        applicationName={detail.vendorName}
        isSubmitting={reviewMutation.isPending}
        errorMessage={reviewError}
        onCancel={closeModals}
        onSubmit={(values) =>
          reviewMutation.mutate(
            {
              applicationId,
              action: 'approve',
              body: values.reason ? { reason: values.reason } : undefined,
            },
            { onSuccess: () => closeModals() },
          )
        }
      />

      <VendorRejectModal
        isOpen={openModal === 'reject'}
        applicationName={detail.vendorName}
        isSubmitting={reviewMutation.isPending}
        errorMessage={reviewError}
        onCancel={closeModals}
        onSubmit={(values) =>
          reviewMutation.mutate(
            { applicationId, action: 'reject', body: { reason: values.reason } },
            { onSuccess: () => closeModals() },
          )
        }
      />

      <VendorRequestResubmissionModal
        isOpen={openModal === 'resubmission'}
        applicationName={detail.vendorName}
        isSubmitting={reviewMutation.isPending}
        errorMessage={reviewError}
        onCancel={closeModals}
        onSubmit={(values) =>
          reviewMutation.mutate(
            {
              applicationId,
              action: 'request-resubmission',
              body: { reason: values.reason, missingFields: values.missingFields },
            },
            { onSuccess: () => closeModals() },
          )
        }
      />

      <VendorCredentialIssueModal
        isOpen={openModal === 'credentials' || openModal === 'reset'}
        isReset={isResetMode}
        applicationName={detail.vendorName}
        defaultEmail={credentialInfo?.initialAdminEmail ?? detail.contactEmail}
        defaultName={credentialInfo?.initialAdminName ?? detail.representativeName}
        isSubmitting={activeCredentialMutation.isPending}
        errorMessage={credentialError}
        result={credentialResult}
        onClose={closeModals}
        onSubmit={(values) =>
          activeCredentialMutation.mutate(
            { applicationId, body: values },
            {
              onSuccess: (res) => {
                setCredentialResult(res);
              },
            },
          )
        }
      />
    </PageShell>
  );
};
